import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useState} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Constants.
 */

const maxTooltipWidth = 240; // px.

/*
 * Props.
 */

interface CreateFormInputDisplayProps {
  className?: string;
  title: ReactNode;
  input: ReactNode;
  isRequired?: boolean;
  isDisabled?: boolean;
  tooltip?: ReactNode;
  onDelete?: Handler;
}

/*
 * Style.
 */

const StyledCreateFormInputDisplayDiv = styled.div`
  margin-top: 20px;
`;

interface StyledTitleDivProps {
  $isDisabled?: boolean;
}

const StyledTitleDiv = styled.div<StyledTitleDivProps>`
  display: flex;
  flex-flow: row;
  gap: 4px;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  margin-bottom: 7px;

  ${(p) =>
    p.$isDisabled &&
    css`
      opacity: 0.5;
    `}
`;

const StyledInputRowDiv = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

interface StyledInputWrapperDivStyleProps {
  $hasDelete: boolean;
}
const StyledInputWrapperDiv = styled.div<StyledInputWrapperDivStyleProps>`
  min-width: ${(p) => (p.$hasDelete ? '90%' : '100%')};
`;

const StyledControlsDiv = styled.div`
  position: absolute;
  right: 0px;
`;

const StyledRequiredIndicatorDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
`;

/*
 * Component.
 */

export const CreateFormInputDisplay: FC<CreateFormInputDisplayProps> = (props) => {
  const theme = useUserTheme();
  const {className, title, input, isRequired, isDisabled, tooltip, onDelete} = props;

  const [shouldShowControls, setShouldShowControls] = useState(false);

  const showControls = () => {
    setShouldShowControls(true);
  };

  const hideControls = () => {
    if (shouldShowControls) {
      setShouldShowControls(false);
    }
  };

  return (
    <StyledCreateFormInputDisplayDiv className={className}>
      <StyledTitleDiv $isDisabled={isDisabled}>
        {title}
        {isRequired && <StyledRequiredIndicatorDiv>*</StyledRequiredIndicatorDiv>}
        {tooltip && <TooltipCoordinator render={() => tooltip} maxWidth={maxTooltipWidth} placement="top" />}
      </StyledTitleDiv>
      <StyledInputRowDiv onMouseEnter={showControls} onMouseLeave={hideControls}>
        <StyledInputWrapperDiv $hasDelete={Boolean(onDelete)}>{input}</StyledInputWrapperDiv>
        {shouldShowControls && onDelete && (
          <StyledControlsDiv>
            <IconButton
              iconName="trashSmall"
              color={theme.greys.shade70}
              label={<Trans id="create-form-input-display-remove">Remove</Trans>}
              onClick={onDelete}
            />
          </StyledControlsDiv>
        )}
      </StyledInputRowDiv>
    </StyledCreateFormInputDisplayDiv>
  );
};
